<template>
  <block-item-card
    ref="chartCard"
    :tooltip="tooltip"
    class="fill-height d-flex flex-column block-item-card"
  >
    <v-row class="ma-0">
      <v-col
        class="ma-0 pa-0 py-5 px-4"
        cols="12"
      >
        <div>
          <div class="chart-header">
            <div class="d-flex flex-column">
              <div class="d-flex align-center">
                <h3
                  class="chart-title"
                  style="color: var(--v-synthesis-ui-grey-01-base);"
                >
                  {{ title }}
                </h3>
                <infotip
                  v-if="tooltip"
                  max-width="200"
                  icon-class="ml-1 mb-1"
                  icon-style="margin-top: 5px"
                >
                  {{ tooltip }}
                </infotip>
              </div>
              <div class="chart-subtitle">
                {{ subtitle }}
              </div>
            </div>
            <div class="pie-chart-container">
              <div class="pie-chart-container__left">
                <p class="ma-0 pa-0">
                  {{ graphLabel }}
                </p>
                <h3
                  class="ma-0 pa-0"
                  :class="{'ratio-red-color': metricValue < 0 && format === 'AMOUNT', 'bleed-end': format === 'AMOUNT', 'metric-value-height': format === 'AMOUNT'}"
                >
                  {{ metricValueFmt }}
                </h3>
              </div>
              <div
                v-if="shouldShowPieChart"
                class="pie-chart-container__right"
              >
                <v-lazy
                  :options="{threshold: .5}"
                  transition="fade-transition"
                >
                  <base-e-chart-pie
                    :chart-data-options="pieData"
                    :chart-height="80"
                    :pie-radius="30"
                    :tooltip-show="false"
                    chart-animation-easing="elasticOut"
                    tooltip-confine
                    :label-line-show="false"
                    :label-show="false"
                    :legend-show="false"
                    :emphasis-disabled="true"
                    :start-angle="0"
                    :default-colors="[themeColorRGBA(lineColor), 'white']"
                    style="pointer-events: none;"
                    :pie-shadow-blur="3"
                    :pie-shadow-color="'rgba(61, 104, 213, 0.15)'"
                    :additional-series="additionalSeries"
                  />
                </v-lazy>
                <v-lazy
                  :options="{threshold: .5}"
                  transition="fade-transition"
                >
                  <div
                    v-if="format === 'RATIO'"
                    style="border: 3px solid white; border-radius: 4px;"
                  >
                    <e-chart
                      :option="ratioChartOptions"
                      class="chart"
                      style="height: 80px; width: 100%;"
                      renderer="canvas"
                      :autoresize="true"
                    />
                  </div>
                </v-lazy>
              </div>
            </div>
          </div>
          <v-lazy>
            <e-chart
              :ref="`echart-${chartRefId}`"
              :autoresize="true"
              :option="chartOptions"
              :style="`height: ${chartHeight}px;`"
              class="chart"
            />
          </v-lazy>
        </div>
      </v-col>
    </v-row>
  </block-item-card>
</template>

    <script>
import BlockItemCard from '@/theme/default/components/common/BlockItemCard'
import baseEChartColors from '@/theme/default/components/charts/echarts/mixins/common/baseEChartColors'
import i18nRouteMeta from '@/mixins/i18n/i18nRouteMeta'
import chartsColorMixin from '@/theme/default/components/charts/mixins/chartsColorMixin'
import riskAnalysisId from '@/theme/default/views/risks/mixins/riskAnalysisId'
import { toNumberFormatNotationShort, computeYAxisBounds, round10 } from '@/lib/utils/helper'
import BaseEChartPie from '@/theme/default/components/charts/echarts/BaseEChartPie'
import Infotip from '@/theme/default/components/common/Infotip'

import { use } from 'echarts/core'
import { BarChart, LineChart } from 'echarts/charts'
import { UniversalTransition } from 'echarts/features'
import { CanvasRenderer } from 'echarts/renderers'

import { GridComponent, LegendComponent, TitleComponent, TooltipComponent, TransformComponent, DatasetComponent } from 'echarts/components'

use([
  BarChart,
  LineChart,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  TransformComponent,
  DatasetComponent,
  CanvasRenderer,
  UniversalTransition
])

export default {
  name      : 'KeyLiquidityRatioBlock',
  components: { BlockItemCard, BaseEChartPie, Infotip },
  directives: {},
  mixins    : [i18nRouteMeta, baseEChartColors, chartsColorMixin, riskAnalysisId],
  props     : {
    // Chart data
    chartData: {
      type    : Object,
      required: true,
      default : () => ({
        dates : [],
        values: [],
        change: 0
      })
    },
    // Chart configuration
    title: {
      type    : String,
      required: true,
      default : ''
    },
    subtitle: {
      type    : String,
      required: true,
      default : ''
    },
    tooltip: {
      type   : String,
      default: ''
    },
    metricLabel: {
      type   : String,
      default: 'Update Interval'
    },
    // Chart styling
    chartHeight: {
      type   : Number,
      default: 250
    },
    lineColor: {
      type   : String,
      default: 'synthesis-ui-green-02'
    },
    areaOpacity: {
      type   : Number,
      default: 0.3
    },
    areaOpacityEnd: {
      type   : Number,
      default: 0.05
    },
    // Chart behavior
    showArea: {
      type   : Boolean,
      default: true
    },
    smoothLine: {
      type   : Boolean,
      default: true
    },
    lineWidth: {
      type   : Number,
      default: 3
    },
    symbolSize: {
      type   : Number,
      default: 10
    },
    format: {
      type   : String,
      default: 'PERCENTAGE'
    },
    baseCurrency: {
      type   : Object,
      default: () => null
    }
  },
  dataStore: {
    appToolbarExtended: 'App.Config.AppToolbarExtended'
  },
  enums: {},
  data () {
    return {
      breakpoint: (this.appToolbarExtended && this.$vuetify.breakpoint.smAndDown ? 12 : 6),
      chartRefId: Date.now()
    }
  },
  computed: {
    graphLabel () {
      return this.format === 'RATIO' ? this.$t('RiskLiquidity.Section.KeyLiquidityRatios.Ratio') : this.$t('RiskLiquidity.Section.KeyLiquidityRatios.Current')
    },
    shouldShowPieChart () {
      return this.format === 'PERCENTAGE'
    },
    additionalSeries () {
      const p = Number(this.metricValue) || 0
      if (p <= 100) return []

      const mainColor  = this.themeColorRGBA(this.lineColor, 1)
      const trackColor = this.themeColorRGBA('synthesis-ui-grey-06')
      const startAngle = 0

      // main disc outer radius you pass to <base-e-chart-pie>
      const mainOuter = 30

      // ring geometry
      const ringThickness = 6
      const ringGap       = 1

      // helper to build a ring showing `fillPct` (0–100)
      const ring = (inner, outer, fillPct) => ({
        type          : 'pie',
        silent        : true,
        hoverAnimation: false,
        label         : { show: false },
        labelLine     : { show: false },
        z             : 1,
        startAngle,
        radius        : [inner, outer],
        data          : [
          { value: fillPct, itemStyle: { color: mainColor } },
          { value: 100 - fillPct, itemStyle: { color: trackColor } }
        ]
      })

      // how much is beyond the first 100%
      let overflow = p - 100
      const series = []
      let i = 0

      while (overflow > 0 && i < 1) { // cap to 5 rings for sanity
        const fill = Math.min(100, overflow)
        const inner = mainOuter + ringGap + i * (ringThickness + ringGap)
        const outer = inner + ringThickness
        series.push(ring(inner, outer, fill))
        overflow -= fill
        i++
      }

      return series
    },

    metricValue () {
      return this.chartData && this.chartData.change !== undefined ? this.chartData.change : 0
    },

    metricValueFmt () {
      switch (this.format) {
        case 'PERCENTAGE':
          return this.metricValue + '%'
        case 'AMOUNT':
          return this.baseCurrency?.displaySymbol + toNumberFormatNotationShort(this.metricValue)
        case 'RATIO':
          return this.metricValue + 'x'
        default:
          return this.metricValue
      }
    },

    pieData () {
      return [
        {
          value: this.metricValue,
          name : this.metricLabel,
          type : 'number'
        },
        {
          value: 100 - this.metricValue,
          name : '100%',
          type : 'number'
        }
      ]
    },

    ratioChartOptions () {
      return {
        legend : { show: false },
        tooltip: { show: false },
        grid   : {
          left        : 0,
          right       : 0,
          top         : 0,
          bottom      : 0,
          containLabel: false
        },
        xAxis: {
          show: false,
          type: 'category',
          data: ['leftBar', 'rightBar']
        },
        yAxis: {
          show: false,
          type: 'value',
          max : this.metricValue
        },
        series: [
          {
            type    : 'bar',
            barWidth: '100%',
            data    : [
              {
                value    : this.metricValue,
                itemStyle: {
                  color       : '#57C05B',
                  borderRadius: [2, 2, 2, 2]
                }
              },
              {
                value    : 1,
                itemStyle: {
                  color       : '#EA804B',
                  borderRadius: [2, 2, 2, 2]
                }
              }
            ]
          }
        ]
      }
    },

    formattedDates () {
      if (!this.chartData || !this.chartData.dates || !Array.isArray(this.chartData.dates)) return []
      return this.chartData.dates.map(date => {
        if (!date) return ''
        const d = new Date(date)
        // Check if the date is valid
        if (isNaN(d.getTime())) return ''
        const month = d.toLocaleDateString('en-US', { month: 'short' })
        const year = d.getFullYear().toString().slice(-2)
        return `${month} ${year}`
      }).filter(date => date !== '') // Remove any invalid dates
    },

    chartOptions () {
      if (!this.chartData || !this.chartData.dates || !this.chartData.values || !Array.isArray(this.chartData.dates) || !Array.isArray(this.chartData.values)) return {}

      return {
        animation        : true,
        animationDuration: 1000,
        animationEasing  : 'quinticOut',
        tooltip          : {
          show           : true,
          trigger        : 'axis',
          backgroundColor: this.themeColorRGBA('synthesis-ui-grey-01', 0.9),
          borderColor    : this.themeColorRGBA('synthesis-ui-grey-01'),
          borderWidth    : 1,
          textStyle      : { color: '#FFFFFF' },
          formatter      : (params) => {
            if (!params[0].value || params[0].value === '-') {
              return null // disables tooltip + pointer
            }
            const value = this.format === 'PERCENTAGE' ? params[0].value + '%' : this.baseCurrency?.displaySymbol + toNumberFormatNotationShort(params[0].value)
            return `<div style="font-weight: bold; margin-bottom: 8px;">${params[0].axisValue}</div>
                        <div style="margin: 4px 0;">
                          <span style="display: inline-block; width: 12px; height: 12px; background: ${params[0].color}; margin-right: 8px;"></span>
                          <span>${this.title}: ${value}</span>
                        </div>`
          }
        },
        legend: { show: false },
        grid  : {
          left        : 25,
          top         : 30,
          right       : 10,
          bottom      : 10,
          containLabel: true
        },
        xAxis: {
          type     : 'category',
          data     : this.formattedDates,
          axisLabel: {
            margin  : 30,
            show    : true,
            color   : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize: 12
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-06', 0.3),
              type : 'dashed'
            }
          }
        },
        yAxis: {
          type     : 'value',
          min      : computeYAxisBounds(this.chartData.values).min,
          max      : computeYAxisBounds(this.chartData.values).max,
          interval : computeYAxisBounds(this.chartData.values).interval,
          axisLabel: {
            margin       : -25,
            show         : true,
            color        : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize     : 11,
            inside       : true,
            verticalAlign: 'bottom',
            formatter    : (value) => {
              return this.format === 'PERCENTAGE' ? round10(value, -1) + '%' : this.baseCurrency?.displaySymbol + (toNumberFormatNotationShort(value) || '0')
            }
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-02', 1),
              type : 'dashed',
              width: 2
            }
          }
        },
        series: [{
          name     : this.title,
          type     : 'line',
          data     : this.chartData.values,
          itemStyle: {
            color: this.themeColorRGBA(this.lineColor)
          },
          lineStyle: {
            width: this.lineWidth
          },
          symbol    : 'emptyCircle',
          symbolSize: this.symbolSize,
          smooth    : this.smoothLine,
          areaStyle : this.showArea ? {
            color: {
              type      : 'linear',
              x         : 0,
              y         : 0,
              x2        : 0,
              y2        : 1,
              colorStops: [
                {
                  offset: 0,
                  color : this.themeColorRGBA(this.lineColor, this.areaOpacity)
                },
                {
                  offset: 1,
                  color : this.themeColorRGBA(this.lineColor, this.areaOpacityEnd)
                }
              ]
            }
          } : undefined
        }]
      }
    }

  },

  watch: {
    appToolbarExtended (val) {
      this.breakpoint = val && this.$vuetify.breakpoint.smAndDown ? 12 : 6
    },

    '$vuetify.breakpoint.smAndDown': {
      handler (val) {
        this.breakpoint = val && this.appToolbarExtended ? 12 : 6
      }
    }
  },
  created () {},
  beforeMount () {},
  mounted () {},
  beforeUpdate () {},
  updated () {},
  destroyed () {},

  methods: {
  }
}
    </script>

    <style scoped>

    .gradient-grey-06-bg {
      background-color: var(--v-synthesis-ui-grey-06-base) !important;
    }

    .chart-title {
      font-size: 12px;
      font-weight: 600;
      line-height: 16px;
    }

    .chart-subtitle {
      font-size: 10px;
      color: var(--v-synthesis-ui-grey-01-base);
    }

    .metric-label {
      font-size: 10px;
      color: var(--v-synthesis-brand-dark-blue-base);
      margin-bottom: 2px;
    }

    .chart-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
    }

    .chart-metric {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
    }

    .metric-value {
      font-size: 12px;
      font-weight: 500;
    }

    .metric-value.positive {
      color: #4CAF50;
      font-size: 28px;
    }

    .metric-value.negative {
      color: #FF5722;
      font-size: 28px;
    }

    .chart-legend {
      display: flex;
      gap: 12px;
    }

    .legend-item {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 10px;
      color: #666;
    }

    .legend-color {
      width: 8px;
      height: 8px;
      border-radius: 2px;
    }

    .block-item-card {
      background-color: var(--v-synthesis-ui-grey-06-base)!important;
    }

    .pie-chart-container {
      display:flex;
      flex-direction: row;
      gap: 16px;
      align-items: center;
    }

    .pie-chart-container__left {
      p {
        font-size: 10px;
        text-align: center
      }

      h3 {
        font-size: 28px !important;
        color: var(--v-synthesis-ui-grey-01-base);
        line-height: 110%;
      }
    }

    .pie-chart-container__right {
       width: 80px
    }

    .pie-chart-container__left h3.ratio-red-color {
      color: var(--v-synthesis-ui-red-02-base);
    }

    .bleed-end {
      margin-right: 16px !important;
    }

    /* Alternative approach with deep selector */
    ::v-deep .pie-chart-container__left h3.ratio-red-color {
      color: var(--v-synthesis-ui-red-02-base);
    }

    .metric-value-height {
      height: 65px;
    }

   </style>
